import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "../0PageStyles.css";
import "./clientPage.css";

//Components
import ItemCard from "../../Components/Item Components/itemCard";
import ItemCardHeader from "../../Components/Item Components/itemCardHeader";
import Title from "../../Components/Other Components/Title";
import SearchBar from "../../Components/Other Components/searchBar";
import SortComponent from "../../Components/Other Components/sortComponent";
import Filter from "../../Components/Other Components/filter";
import { SelectionCategory } from "../../Components/Other Components/Category";
import PageLoader from "../../Components/Other Components/pageLoader";
import Button from "../../Components/Other Components/button";
import ExportData from "../../Components/Other Components/ExportDataComponent";

//Functions and hooks
import {
  GetClients,
  CheckForClient,
  GetColumnValues,
} from "../../BackendAccess/ClientHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { useTranslation } from "react-i18next";

export default function ClientPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categoryArray, setCategoryArray] = useState([]);
  const [types, setTypes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredItems, setSearchFilteredItems] = useState([]);

  const lazyLoadItemLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  const fetchClients = async () => {
    setLoading(true);
    try {
      const [clientsData, clientsError] = await fetchWithTimeout(GetClients);
      if (clientsError) {
        clientsError.isPageError = true;
        setError(clientsError);
      } else setClients(clientsData);

      const [typesData, typesError] = await fetchWithTimeout(() =>
        GetColumnValues("Type")
      );
      if (typesError) {
        typesError.isPageError = true;
        setError(typesError);
      } else setTypes(typesData.map((item) => item.Type));

      const [banksData, banksError] = await fetchWithTimeout(() =>
        GetColumnValues("Bank")
      );
      if (banksError) {
        banksError.isPageError = true;
        setError(banksError);
      } else setBanks(banksData.map((item) => item.Bank));
    } catch (error) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (clients.length > 0) setFilteredItems(clients);
  }, [clients]);
  useEffect(() => {
    if (types.length > 0 && banks.length > 0) {
      createCategoryArray();
    }
  }, [types, banks]);

  const handleInfoClick = async (id) => {
    const [bItemExists, itemExistsError] = await CheckForClient(id);
    if (itemExistsError) {
      setError(itemExistsError);
      return;
    }
    if (bItemExists) {
      navigate("/workspace/clients/clientInfoPage", { state: { id } });
    } else {
      console.log("CLIENTSSS", bItemExists);
      setError({
        err: t("Client not found.", { ns: "clients" }),
        errCode: 404,
      });
    }
  };
  const handleCreateClick = async () => {
    navigate("/workspace/clients/clientCreationPage");
  };

  //***************************SEARCHING AND SORTING******************************
  const createCategoryArray = () => {
    const newCatArr = [
      new SelectionCategory(
        "Bank",
        banks.map((item) => {
          return { ItemName: item, selected: false };
        }),
        false,
        ItemHasOptionBank
      ),
      new SelectionCategory(
        "Type",
        types.map((item) => {
          return { ItemName: item, selected: false };
        }),
        false,
        ItemHasOptionType,
        true
      ),
    ];
    setCategoryArray(newCatArr);
  };
  const ItemHasOptionBank = (item, bank) => {
    return item.Bank === bank;
  };

  const ItemHasOptionType = (item, type) => {
    return item.Type === type;
  };

  const setNewCategoryArray = (newCategoryArray) => {
    setCategoryArray(newCategoryArray);
  };

  const setNewItems = (newItems) => {
    setFilteredItems(newItems);
  };

  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter items based on search term
  useEffect(() => {
    const newSearchFilteredItems = filteredItems.filter((item) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        item.Name.toLowerCase().includes(lowerCaseTerm) ||
        item.ClientID.toString().toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredItems(newSearchFilteredItems);
  }, [searchTerm, filteredItems]);

  const nameSortFunction = (direction) => {
    var clientsSorted = [...filteredItems].sort((a, b) =>
      a.Name.toUpperCase() > b.Name.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setClients(clientsSorted);
  };
  const idSortFunction = (direction) => {
    var clientsSorted = [...filteredItems].sort((a, b) =>
      a.ClientID > b.ClientID ? direction * 1 : -1 * direction
    );
    setClients(clientsSorted);
  };
  const typeSortFunction = (direction) => {
    var clientsSorted = [...filteredItems].sort((a, b) =>
      a.Type.toUpperCase() > b.Type.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setClients(clientsSorted);
  };
  const bankSortFunction = (direction) => {
    var clientsSorted = [...filteredItems].sort((a, b) =>
      a.Bank.toUpperCase() > b.Bank.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setClients(clientsSorted);
  };

  //***************************SEARCHING AND SORTING END******************************

  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredItems,
    lazyLoadItemLoadCount,
    lazyLoadPixelThreshold
  );
  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  ////////////////////////////////////////Creating cards///////////////////////////////////////////
  const propertiesToShow = {
    ID: { Show: true, Width: 2 },
    Name: { Show: true, Width: 3 },
    Type: { Show: true, Width: 2 },
    Bank: { Show: true, Width: 3 },
    BankAccount: { Show: true, Width: 3 },
  };

  const columnWidths = Object.values(propertiesToShow).map(
    (item) => item.Width
  );
  const createHeaderCardProps = () => {
    const cardPropArray = [
      { Value: "ID", columnWidth: columnWidths[0] },
      { Value: "Name", columnWidth: columnWidths[1] },
      { Value: "Type", columnWidth: columnWidths[2] },
      ...(isNarrow ? [] : [{ Value: "Bank", columnWidth: columnWidths[3] }]),
      ...(isNarrow
        ? []
        : [{ Value: "BankAccount", columnWidth: columnWidths[4] }]),
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Value].Show;
    });
    return newCardPropArr;
  };
  const createCardProps = (item) => {
    const cardPropArray = [
      { Name: "ID", Value: item.ClientID, columnWidth: columnWidths[0] },
      { Name: "Name", Value: item.Name, columnWidth: columnWidths[1] },
      {
        Name: "Type",
        Value: item.Type,
        columnWidth: columnWidths[2],
        customViewFunction: (item) => t(item),
      },
      ...(isNarrow
        ? []
        : [
            {
              Name: "Bank",
              Value: item.Bank,
              columnWidth: columnWidths[3],
            },
          ]),
      ...(isNarrow
        ? []
        : [
            {
              Name: "BankAccount",
              Value: item.BankAccount,
              columnWidth: columnWidths[4],
            },
          ]),
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Name].Show;
    });
    return newCardPropArr;
  };

  ////////////////////////////////////////Creating cards end///////////////////////////////////////////

  const tutorialSteps = [
    {
      target: null,
      title: t("Your clients", { ns: "tutorial" }),
      message: t(
        "This is your Clients page - suppliers, manufacturers, and buyers you do business with. You'll link them to items and traffic notes to track where stock comes from and where it goes.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".search-bar-container",
      title: t("Search", { ns: "tutorial" }),
      message: t("Search for a client by name or ID.", { ns: "tutorial" }),
    },
    {
      target: ".sortAndFilterBar",
      title: t("Sort and filter", { ns: "tutorial" }),
      message: t(
        "Sort the list by name, ID, type, or bank, and use Filters to narrow clients down by type or bank.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".CardHeader .btn-positive",
      title: t("Create a client", { ns: "tutorial" }),
      message: t(
        "Add a new supplier, manufacturer, or buyer here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-card-container .Card:first-child",
      title: t("Client rows", { ns: "tutorial" }),
      message: t(
        "Each row is one client, with its ID, name, type, and bank details. Click Edit to see or change its details.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".export-container",
      title: t("Export", { ns: "tutorial" }),
      message: t(
        "Export the current client list to Excel or CSV - this respects whatever search, filter, and sort you've currently applied.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="clients" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">Clients</Title>
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search clients...", { ns: "clients" })}
            />
          </div>
          <div className="sortAndFilterBar">
            <SortComponent
              sortOpts={["Name", "ID", "Type", "Bank"]}
              sortOptFunctions={[
                nameSortFunction,
                idSortFunction,
                typeSortFunction,
                bankSortFunction,
              ]}
            />
            <Filter
              categoryArray={categoryArray}
              ConstItems={clients}
              Items={filteredItems}
              setNewItems={setNewItems}
              setNewCatArr={setNewCategoryArray}
            />
          </div>
          <div className="item-card-and-header-container">
            <ItemCardHeader
              propertiesToShow={createHeaderCardProps()}
              bBtnVisible={true}
              bBtnSectionVisible={true}
              btnSectionWidth={1}
              onClick={handleCreateClick}
              btnText={"Create"}
            />
            <div className="item-card-container" ref={itemContainerRef}>
              {loadedItems.map((item) => (
                <ItemCard
                  key={item.ClientID}
                  ItemID={item.ClientID}
                  propertiesToShow={createCardProps(item)}
                  bBtnVisible={true}
                  bBtnSectionVisible={true}
                  btnSectionWidth={1}
                  onClick={() => {
                    handleInfoClick(item.ClientID);
                  }}
                  btnText={t("Edit")}
                />
              ))}
            </div>
          </div>
          {isNarrow ? (
            <div className="item-view-narrow-btn-container">
              <Button
                btnStyle={"btn-positive"}
                icon="create"
                onClick={handleCreateClick}
              >
                {t("Create")}
              </Button>
            </div>
          ) : null}
          {<ExportData data={searchFilteredItems} />}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
